import type { SignedInUserResource } from '@repo/api-client';

/**
 * What a route says about who may see it, and the answer for one viewer.
 *
 * This is the browser half of the API's deny-by-default authorisation. The
 * server declares every route with `@PublicRoute()`, `@RequireRole('OWNER')` or
 * `@RequireOutletScope()`, refuses anything undeclared, and will not start at
 * all with an undeclared route in it. The same shape is worth having here for
 * one reason: a screen that renders a control the caller may not use teaches
 * staff to click things that refuse, and a screen that renders nothing at all
 * teaches them the app is broken.
 *
 * **None of this is authorisation.** The server refuses regardless of what this
 * file decides, and it is the only thing that can — the role read here comes
 * from the session restored out of `sessionStorage`, which is validated for
 * shape and not for truth, survives a reload, and is editable by anyone with the
 * developer tools open. Everything decided here is cosmetic in the precise
 * sense that changing it changes what is *drawn*, never what is *permitted*.
 * Every screen still handles `AUTH_FORBIDDEN` arriving.
 *
 * Pure: no React, no router, no clock, no storage. Everything that decides
 * whether a route renders is one function of two values, so the interesting
 * cases can be read off it rather than inferred from a component tree.
 */

/**
 * The four roles, from the generated client rather than restated.
 *
 * `SignedInUserResource` is what `POST /v1/auth/sign-in` answers with, so this
 * union changes when the API's does and a role added there fails to compile
 * here instead of silently never matching.
 */
export type Role = SignedInUserResource['role'];

/**
 * The signed-in caller, reduced to the two facts a route guard reads.
 *
 * `null` everywhere below means nobody is signed in — deliberately a separate
 * value rather than a role, so "signed out" cannot be confused with "signed in
 * as somebody with no permissions".
 */
export interface Viewer {
  readonly role: Role;

  /**
   * Whether they must replace the credential they were handed (FR-107).
   *
   * The API refuses *every* route except the public ones and the one that does
   * the replacing until this is false, so a screen shown to such a person is a
   * screen where everything 403s.
   */
  readonly mustChangeCredential: boolean;
}

/**
 * What a route declares. One of four, and a route must pick one.
 *
 * A discriminated union rather than a bag of optional booleans, so "declared
 * nothing" is not a state the type can express and every reachable combination
 * has a name. The API reaches the same place from the other direction: it keeps
 * its metadata under separate keys and treats the nearest declaration as
 * winning *whole*, because merging two partial declarations is how one silently
 * overwrites the other.
 *
 * There is no mirror of `@RequireOutletScope()` here. That declaration is about
 * which *rows* a query may return, and the API enforces it in the WHERE clause
 * rather than at the door — a screen is reachable or it is not, and the outlet
 * boundary shows up as a shorter list, not a refusal.
 */
export type AccessDeclaration =
  /**
   * Reachable by anybody, signed in or not — the mirror of `@PublicRoute()`.
   * The not-found screen is one: sending a mistyped URL to sign-in, and then
   * "returning" the user to a page that does not exist, helps nobody.
   */
  | { readonly kind: 'public' }

  /**
   * For people with no session. Sign-in is the only one today.
   *
   * Distinct from `public` because a signed-in person arriving here is somewhere
   * that cannot do anything for them: signing in again mints a second session
   * and orphans the first, which stays live server-side for the rest of its
   * twelve hours. They are sent on instead.
   */
  | { readonly kind: 'signedOut' }

  /**
   * Any signed-in user — the mirror of `@RequireRole()` with no arguments.
   *
   * Spelling this as all four roles would have to be revisited every time a role
   * is added and would quietly mean the wrong thing until somebody did.
   */
  | {
      readonly kind: 'signedIn';

      /**
       * Reachable by somebody who has not yet replaced the credential they were
       * handed — the mirror of `@AllowsPendingCredential()`.
       *
       * True for exactly one route, the one that does the replacing (FE007).
       * It is not a declaration of its own: a route carrying only this has still
       * said nothing about who may reach it.
       */
      readonly allowsPendingCredential: boolean;
    }

  /**
   * One of `roles` — the mirror of `@RequireRole('OWNER')`. The whole
   * identity-admin surface (outlets, terminals, users, assignments) is
   * `OWNER`-only.
   */
  | { readonly kind: 'role'; readonly roles: readonly Role[] };

/** Reachable by anybody. Declared, never inferred. */
export const publicRoute = (): AccessDeclaration => ({ kind: 'public' });

/** For people with no session. A signed-in one is sent on. */
export const signedOutOnly = (): AccessDeclaration => ({ kind: 'signedOut' });

/** Any signed-in user, whatever their role. */
export const requireSignedIn = (
  options: { readonly allowsPendingCredential?: boolean } = {},
): AccessDeclaration => ({
  kind: 'signedIn',
  allowsPendingCredential: options.allowsPendingCredential ?? false,
});

/**
 * One of `roles`.
 *
 * The non-empty tuple parameter is load-bearing: `requireRole()` naming nobody
 * would refuse every caller, which renders as a screen that will not open for
 * anyone and reads as a bug in the screen. The audit re-checks it at runtime,
 * because a type is not a guard against a cast.
 */
export const requireRole = (...roles: readonly [Role, ...Role[]]): AccessDeclaration => ({
  kind: 'role',
  roles,
});

/** Every declaration kind this app knows how to act on. */
const KINDS: readonly string[] = ['public', 'signedOut', 'signedIn', 'role'];

/**
 * Whether `access` is a declaration this app recognises.
 *
 * Takes `unknown` because the point is the values the type system did not see:
 * a cast, a stale build of one package against another, a route table assembled
 * at runtime. Anything that is not one of the four kinds has declared nothing,
 * whatever it claims.
 */
export function isRecognisedDeclaration(access: unknown): access is AccessDeclaration {
  if (typeof access !== 'object' || access === null) return false;

  const { kind } = access as { kind?: unknown };

  return typeof kind === 'string' && KINDS.includes(kind);
}

/**
 * What should happen to `viewer` on a route declaring `access`.
 *
 * The order of the checks is the behaviour, so it is worth reading as a
 * sequence rather than a set:
 *
 * 1. **Undeclared is refused**, before anything else is considered. No
 *    credential would have helped and none is asked for.
 * 2. **Public is admitted**, including for somebody mid-credential-change —
 *    matching the API, whose gate exempts its public routes.
 * 3. **The credential gate comes before the role check**, so a provisioned user
 *    is sent to replace their credential rather than being told they lack a
 *    role. It is the same refusal on the server and the more useful sentence.
 * 4. **A missing session sends them to sign in; a wrong role does not.** Signing
 *    in again cannot give somebody a role they do not hold, and bouncing a
 *    signed-in user to a sign-in screen they are already past is how a redirect
 *    loop starts.
 */
export type AccessOutcome =
  /** Draw it. */
  | 'render'

  /** Nobody is signed in and this route needs somebody. Go and sign in. */
  | 'sign-in-first'

  /** Signed in, and this route is not for them. A refusal, not a redirect. */
  | 'refused'

  /** Signed in, on a signed-out route. Go where they were going instead. */
  | 'already-signed-in'

  /** Signed in on a credential they must replace before using anything else. */
  | 'must-change-credential'

  /** The route said nothing, or nothing this app understands. Refused loudly. */
  | 'undeclared';

export function decideAccess(
  access: AccessDeclaration | null | undefined,
  viewer: Viewer | null,
): AccessOutcome {
  if (!isRecognisedDeclaration(access)) return 'undeclared';

  if (access.kind === 'public') return 'render';

  if (viewer === null) {
    // A signed-out route is exactly where a signed-out person belongs.
    return access.kind === 'signedOut' ? 'render' : 'sign-in-first';
  }

  const exempt = access.kind === 'signedIn' && access.allowsPendingCredential;

  if (viewer.mustChangeCredential && !exempt) return 'must-change-credential';

  if (access.kind === 'signedOut') return 'already-signed-in';

  if (access.kind === 'signedIn') return 'render';

  // A role list naming nobody refuses everybody. The audit calls that a defect
  // at start-up; if one reaches here anyway it is refused rather than admitted,
  // because a declaration this app cannot read is not a declaration.
  if (access.roles.length === 0) return 'undeclared';

  return access.roles.includes(viewer.role) ? 'render' : 'refused';
}

/**
 * Whether a route needs a session, and so belongs inside the back-office shell.
 *
 * Stated as a function rather than repeated at each call site: "which routes get
 * navigation" and "which routes require a session" are the same question today,
 * and the day they stop being the same question this is the one line to change.
 */
export function needsSession(access: AccessDeclaration): boolean {
  return access.kind === 'signedIn' || access.kind === 'role';
}
